import { SLIDER_CONFIG, SELECTORS } from './Constants';
import SliderManager from './SliderManager';
import ArrowVisibilityManager from './ArrowVisibilityManager';
import ArticleMover from './ArticleMover';
import SliderConfigBuilder from './SliderConfigBuilder';
import type { SlickConfig, FeatureArticleElements } from './Types';

/**
 * Feature Article Component Manager
 * Orchestrates all feature article functionality
 */
export default class FeatureArticleComponent {

    component: JQuery<HTMLElement>;
    initialized: boolean;
    elements: FeatureArticleElements | null;
    hasComponentBody: boolean;
    sliderConfig: SlickConfig | null;
    sliderManager: SliderManager | null;
    arrowManager: ArrowVisibilityManager | null;
    articleMover: ArticleMover | null;
    mobileQuery: MediaQueryList;
    /**
     * @param component - Main component element
     */
    constructor(component: JQuery<HTMLElement>) {
        this.component = component;
        this.initialized = false;
        this.elements = this._gatherElements();

        if (!this._validateElements() || !this.elements) {
            this.hasComponentBody = false;
            this.sliderConfig = null;
            this.sliderManager = null;
            this.arrowManager = null;
            this.articleMover = null;
            this.mobileQuery = window.matchMedia(SLIDER_CONFIG.MOBILE_BREAKPOINT);
            return;
        }

        this.hasComponentBody = this._checkComponentBody();
        this.sliderConfig = this._buildSliderConfig();
        this.sliderManager = new SliderManager(
            this.elements.articleSlides,
            this.sliderConfig,
            this.elements.articlesContainer,
            this.component
        );
        this.arrowManager = new ArrowVisibilityManager(this.component);
        this.articleMover = new ArticleMover(
            this.elements.featureColumn,
            this.elements.articleSlides
        );
        this.mobileQuery = window.matchMedia(SLIDER_CONFIG.MOBILE_BREAKPOINT);
    }

    /**
     * Initializes the component
     */
    initialize(): void {
        if (this.initialized || !this.elements) {
            return;
        }

        this._cleanupExistingSlick();
        this._initialArticlePlacement();
        this._moveControlWrapper();
        this._initializeSlider();
        this._setupArrowObservers();
        this._setupResponsiveHandling();
        this._setupResizeHandler();

        this.initialized = true;
    }

    private _setupResizeHandler(): void {
        if (!this.component.hasClass('news-feed-feature-rows')) {
            return;
        }

        let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
        const componentId = this.component.attr('id') || Math.random().toString(36).substr(2, 9);
        $(window).on(`resize.featureRows${componentId}`, () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (!this.mobileQuery.matches) {
                    this._matchFeatureRowsHeight();
                }
            }, 150);
        });
    }

    /**
     * Cleans up component resources
     */
    cleanup(): void {
        if (this.arrowManager) {
            this.arrowManager.cleanup();
        }

        // Clean up resize handler for Feature Rows
        if (this.component.hasClass('news-feed-feature-rows')) {
            const componentId = this.component.attr('id') || '';
            $(window).off(`resize.featureRows${componentId}`);

            // Clear height
            if (this.elements) {
                const slickList = this.elements.articlesContainer.find('.slick-list');
                if (slickList.length) {
                    slickList.css('height', '');
                }
            }
        }
    }

    /**
     * Gathers all DOM elements needed by the component
     * @returns Elements object
     */
    private _gatherElements(): FeatureArticleElements {
        const articlesContainer = this.component.find(SELECTORS.ARTICLES_CONTAINER);

        return {
            articlesContainer: articlesContainer,
            articleSlides: this.component.find(SELECTORS.SLIDES_CONTAINER),
            controlsContainer: this.component.find(SELECTORS.CONTROLS_CONTAINER),
            featureColumn: articlesContainer.siblings(SELECTORS.COMPONENT_COLUMN),
            prevArrow: this.component.find(SELECTORS.PREV_ARROW),
            nextArrow: this.component.find(SELECTORS.NEXT_ARROW),
            mobilePrevArrow: this.component.find(SELECTORS.MOBILE_PREV_ARROW),
            mobileNextArrow: this.component.find(SELECTORS.MOBILE_NEXT_ARROW)
        };
    }

    private _validateElements(): boolean {
        if (!this.elements) return false;

        const { articlesContainer, articleSlides, controlsContainer } = this.elements;

        if (!articlesContainer.length || !articleSlides.length || !controlsContainer.length) {
            return false;
        }

        const articleItems = articleSlides.find('article');
        return articleItems.length > 0;
    }

    private _checkComponentBody(): boolean {
        const container = this.component.closest(SELECTORS.COMPONENT_CONTENT);
        return container.attr('data-comp-body') === 'true';
    }

    /**
     * Builds Slick configuration
     * @returns Slick configuration
     */
    private _buildSliderConfig(): SlickConfig {
        if (!this.elements) {
            throw new Error('Elements not initialized');
        }

        const configBuilder = new SliderConfigBuilder(
            this.elements.prevArrow,
            this.elements.nextArrow,
            this.elements.mobilePrevArrow,
            this.elements.mobileNextArrow
        );
        return configBuilder.build();
    }

    private _cleanupExistingSlick(): void {
        if (!this.elements) return;

        const { articleSlides } = this.elements;

        if (articleSlides.hasClass('slick-initialized') && typeof articleSlides.slick === 'function') {
            try {
                articleSlides.slick('unslick');
            } catch {
                // Ignore cleanup errors
            }
        }
    }

    private _initialArticlePlacement(): void {
        if (!this.elements) return;
        const initialMobile = this.mobileQuery.matches;

        // On desktop without component body, move first article to feature column
        if (!initialMobile && !this.hasComponentBody) {
            const firstArticle = this.elements.articleSlides.find('article').first();
            if (firstArticle.length) {
                this.elements.featureColumn.append(firstArticle);
            }
        }
    }

    private _moveControlWrapper(): void {
        if (!this.elements) return;
        const controlWrapper = this.component.find(SELECTORS.CONTROL_WRAPPER);

        if (controlWrapper.length) {
            this.elements.controlsContainer.append(controlWrapper.detach());
        }
    }

    private _initializeSlider(): void {
        if (!this.sliderManager) return;

        this.sliderManager.init();

        // Match heights for Feature Rows after Slick initializes
        if (this.component.hasClass('news-feed-feature-rows') && !this.mobileQuery.matches) {
            setTimeout(() => this._matchFeatureRowsHeight(), 200);
        }
    }

    private _setupArrowObservers(): void {
        if (!this.arrowManager || !this.elements) return;

        this.arrowManager.observeArrow(this.elements.prevArrow, 'desktopPrev');
        this.arrowManager.observeArrow(this.elements.nextArrow, 'desktopNext');
        this.arrowManager.observeArrow(this.elements.mobilePrevArrow, 'mobilePrev');
        this.arrowManager.observeArrow(this.elements.mobileNextArrow, 'mobileNext');
    }

    private _setupResponsiveHandling(): void {
        const handleBreakpointChange = (event: MediaQueryListEvent): void => {
            if (!this.arrowManager || !this.sliderManager || !this.articleMover || !this.elements) return;
            this.arrowManager.cleanup();
            this.sliderManager.destroy();

            // Rebuild container for clean state
            this.elements.articleSlides = this.sliderManager.rebuildContainer();

            // Recreate article mover with updated slides
            this.articleMover = new ArticleMover(
                this.elements.featureColumn,
                this.elements.articleSlides
            );

            if (event.matches) {
                // Mobile: all articles in slider
                if (!this.hasComponentBody) {
                    this.articleMover.moveToSlider();
                }
                this.sliderManager.reinit();
                this.arrowManager.setVisibility(true);
            } else {
                // Desktop: feature article in column
                if (!this.hasComponentBody) {
                    this.articleMover.moveToFeature();
                }
                this.sliderManager.reinit();
                this.arrowManager.setVisibility(false);

                // Match heights for Feature Rows after reinit
                if (this.component.hasClass('news-feed-feature-rows')) {
                    setTimeout(() => this._matchFeatureRowsHeight(), 150);
                }
            }

            this._setupArrowObservers();
        };

        // Bind event listener
        if (this.mobileQuery.addEventListener) {
            this.mobileQuery.addEventListener('change', handleBreakpointChange);
        } else if (this.mobileQuery.addListener) {
            // Legacy support
            this.mobileQuery.addListener(handleBreakpointChange);
        }
    }

    private _matchFeatureRowsHeight(): void {
        // Only match heights when component body is not being used
        if (!this.elements || !this.component.hasClass('news-feed-feature-rows') || this.hasComponentBody) {
            return;
        }

        const featureImg = this.elements.featureColumn.find('.ss-post-feed-img-container').first();
        const slickList = this.elements.articlesContainer.find('.slick-list');

        if (featureImg.length && slickList.length) {
            const imgHeight = featureImg.outerHeight();
            if (imgHeight !== undefined && imgHeight > 0) {
                slickList.css('height', `${imgHeight}px`);
            }
        }
    }
}

